import produce from 'immer';
import _ from 'lodash';
import moment from 'moment';
import { types, IMessageInitialState } from './types';

const defaultPagination = {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 20,
};

const initialState: IMessageInitialState = {
    users: [],
    rooms: [],
    selectedRoomMessages: [],
    roomMsgError: null,
    fetchingRoomMsg: false,
    fetchingRooms: false,
    fetchingMessages: false,
    sendingMessage: false,
    gettingMessages: false,
    gettingMessagesError: null,
    isUserTyping: 'no',
    newMessage: false,
    sendingMsgError: null,
    onlineUsers: [],
    newContentAvailable: false,
    newContentType: '',
    roomsPagination: defaultPagination,
    messagesPagination: defaultPagination,
    searching: false,
    searchError: null,
    isSearchActive: false,
    searchTerm: '',
    fetchingMoreRooms: false,
    fileUploadQueue: [],
};

const reducer = (state = initialState, action: any) => {
    const { type, payload } = action;
    switch (type) {
        case types.RETRIEVE_ROOMS:
            return produce(state, draftState => {
                draftState.rooms = [];
                draftState.fetchingRooms = true;
            });
        case types.GET_MY_ROOMS:
            return produce(state, draftState => {
                const { page } = payload;
                if (page < 2) {
                    draftState.rooms = [];
                    draftState.fetchingRooms = true;
                } else {
                    draftState.fetchingMoreRooms = true;
                }
            });
        case types.GET_ALL_MESSAGES:
            return produce(state, draftState => {
                draftState.gettingMessages = true;
            });
        case types.GET_ALL_MESSAGES_SUCCESS:
        case types.GET_MY_ROOMS_SUCCESS:
            return produce(state, draftState => {
                const { rooms, pagination } = payload;
                // If it's the first page, replace rooms. Otherwise, append
                if (pagination.currentPage === 1) {
                    draftState.rooms = rooms;
                    draftState.fetchingRooms = false;
                } else {
                    draftState.rooms = [...draftState.rooms, ...rooms];
                    draftState.fetchingMoreRooms = false;
                }
                draftState.roomsPagination = pagination;
            });
        case types.GET_ALL_MESSAGES_FAIL:
        case types.GET_MY_ROOMS_FAIL:
            return produce(state, draftState => {
                draftState.gettingMessagesError = payload;
                draftState.fetchingRooms = false;
                draftState.fetchingMoreRooms = false;
            });
        case types.SET_ROOM_MESSAGES:
            return produce(state, draftState => {
                // Only clear messages if it's the first page
                if (!payload.pagination || payload.pagination.currentPage === 1) {
                    // draftState.selectedRoomMessages = [];
                    // draftState.fetchingRoomMsg = true;
                    // draftState.loadingMore = false
                }
            });
        case types.SET_ROOM_MESSAGES_SUCCESS:
            return produce(state, draftState => {
                const { messages, pagination } = payload;
                const sortedMessageArray = _.orderBy(messages, (m: any) => moment(m.createdAt), ['desc']);

                // If it's the first page, replace messages. Otherwise, append
                if (pagination.currentPage === 1) {
                    draftState.selectedRoomMessages = sortedMessageArray;
                } else {
                    draftState.selectedRoomMessages = [...draftState.selectedRoomMessages, ...sortedMessageArray];
                }
                draftState.messagesPagination = pagination;
                draftState.fetchingMessages = false;
                draftState.loadingMore = false;
            });
        case types.SET_ROOM_MESSAGES_FAIL:
            return produce(state, draftState => {
                draftState.fetchingRoomMsg = false;
                draftState.roomMsgError = payload;
                draftState.fetchingMessages = false;
            });
        case types.USER_TYPING:
        case types.USER_STOP_TYPING:
            return produce(state, draftState => {
                const { status, roomID, userId } = payload;
                draftState.isUserTyping = status;
                const findIndexForMsg: any = draftState.rooms.findIndex(room => room._id === roomID);
                draftState.rooms[findIndexForMsg].isUserTyping = status;
                const userTypingObject = draftState.rooms[findIndexForMsg]?.userTyping ?? [];
                if (status === 'yes') {
                    draftState.rooms[findIndexForMsg].userTyping = [...userTypingObject, userId];
                } else {
                    draftState.rooms[findIndexForMsg].userTyping = draftState.rooms[findIndexForMsg].userTyping?.filter(
                        id => id !== userId,
                    );
                }
            });
        case types.SEND_MESSAGE:
            return produce(state, draftState => {
                draftState.newMessage = false;
                const messageObject = {
                    ...payload,
                    created_by: payload.userObject,
                    _id: payload.uniqueSocketID,
                    parent_message: payload.replyingToObject,
                    is_reply: !!payload.replyingToObject,
                    // Add properly formatted attachment data based on message type
                    ...(payload.message_type === 'picture_message' && {
                        image: payload.image || [],
                        isLoading: true,
                    }),
                    ...(payload.message_type === 'document_message' && {
                        document: payload.document || [],
                        isLoading: true,
                    }),
                    ...(payload.message_type === 'video_message' && {
                        media: payload.tempProjects || [],
                        tempProjects: payload.tempProjects || [],
                    }),
                    ...(payload.message_type === 'sound_message' && {
                        audio: payload.tempAudios || [],
                        tempAudios: payload.tempAudios || [],
                    }),
                    ...(payload.message_type === 'location_message' && {
                        location: payload.location || {},
                    }),
                };
                if (
                    payload &&
                    payload.room ===
                        draftState.selectedRoomMessages[draftState.selectedRoomMessages.length - 1]?.room?._id
                ) {
                    draftState.selectedRoomMessages = [messageObject, ...draftState.selectedRoomMessages];
                }
            });
        case types.SEND_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                /** get The ID of the message */
                const messageIndex = draftState.selectedRoomMessages.findIndex(
                    msg => msg.uniqueSocketID === payload.uniqueSocketID,
                );
                if (messageIndex > -1) {
                    draftState.selectedRoomMessages[messageIndex] = payload;
                } else if (payload?.room?._id === draftState.selectedRoomMessages[0]?.room?._id) {
                    draftState.selectedRoomMessages.unshift(payload);
                }

                const findIndexForMsg: number = draftState.rooms.findIndex(room => room._id === payload?.room._id);
                if (findIndexForMsg > -1 && draftState.rooms[findIndexForMsg]?.messages) {
                    draftState.rooms[findIndexForMsg].messages.unshift(payload);
                    draftState.rooms[findIndexForMsg].unread_message_count = payload.newRoom.unread_message_count;
                } else {
                    draftState.rooms[findIndexForMsg] = {
                        ...draftState.rooms[findIndexForMsg],
                        messages: [payload],
                        unread_message_count: payload.newRoom.unread_message_count,
                    };
                }

                const newDetails = {
                    read_by: payload.newRoom.read_by,
                    last_message_at: payload.newRoom.last_message_at,
                };
                draftState.rooms[findIndexForMsg] = {
                    ...draftState.rooms[findIndexForMsg],
                    ...newDetails,
                };
                draftState.newMessage = true;
            });
        case types.SEND_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.newMessage = false;
                const messageIndex = draftState.selectedRoomMessages.findIndex(
                    msg => msg.uniqueSocketID === payload.uniqueSocketID,
                );
                if (messageIndex > -1) {
                    draftState.selectedRoomMessages.filter((_, index) => index !== messageIndex);
                }
            });
        case types.INITIATE_MESSAGE:
            return produce(state, draftState => {
                draftState.sendingMessage = true;
            });
        case types.INITIATE_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.sendingMessage = false;

                const roomIndex = draftState.rooms.findIndex(room => room._id === payload._id);

                if (roomIndex > -1) {
                    // Replace existing room
                    draftState.rooms[roomIndex] = payload;
                } else {
                    // Add new room to the top
                    draftState.rooms.unshift(payload);
                }

                // Update selectedRoomMessages if this is the current room
                if (draftState.selectedRoomMessages[0]?.room?._id === payload._id) {
                    // Ensure no duplicate message IDs in selectedRoomMessages
                    const firstMessageId = payload.messages[0]?._id;
                    const exists = draftState.selectedRoomMessages.some(msg => msg._id === firstMessageId);

                    if (!exists && payload.messages.length > 0) {
                        draftState.selectedRoomMessages.unshift(payload.messages[0]);
                    }
                }
            });

        case types.INITIATE_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.sendingMessage = false;
                draftState.sendingMsgError = payload;
            });
        case types.SET_ONLINE_USERS:
            return produce(state, draftState => {
                draftState.onlineUsers = [...new Set([...draftState.onlineUsers, payload])] as any;
            });
        case types.SET_OFFLINE_USER:
            return produce(state, draftState => {
                draftState.onlineUsers = draftState.onlineUsers.filter(user => user !== payload);
            });
        case types.NEW_CONTENT_AVAILABLE:
            return produce(state, draftState => {
                draftState.newContentAvailable = true;
                draftState.newContentType = payload;
            });
        case types.RESET_NEW_CONTENT:
            return produce(state, draftState => {
                draftState.newContentAvailable = false;
                draftState.newContentType = '';
            });
        case types.RETRIEVE_USER_ROOMS:
            return produce(state, draftState => {
                const {
                    data: {
                        query: { page },
                    },
                } = payload;
                if (page && page < 2) {
                    draftState.fetchingRooms = true;
                }
            });
        case types.RETRIEVE_USER_ROOMS_SUCCESS:
            return produce(state, draftState => {
                draftState.fetchingRooms = false;
            });
        case types.RETRIEVE_USER_ROOMS_FAIL:
            return produce(state, draftState => {
                draftState.fetchingRooms = false;
            });
        case 'LOGOUT':
            return produce(state, draftState => {
                draftState.onlineUsers = [];
            });
        case types.GET_MESSAGES_FOR_SELECTED_ROOM:
            return produce(state, draftState => {
                if (payload.pagination.page < 2) {
                    draftState.fetchingMessages = true;
                } else {
                    draftState.loadingMore = true;
                }
            });
        case types.UPDATE_UNREAD_COUNT:
            return produce(state, draftState => {
                const { roomID, readBy } = payload;
                if (roomID && readBy) {
                    const selectedRoomIndex = draftState.rooms.findIndex(room => room._id === roomID);
                    if (selectedRoomIndex > -1) {
                        const room = draftState.rooms[selectedRoomIndex];

                        if (!room.unread_message_count) {
                            room.unread_message_count = [];
                        }

                        const userIndex = room.unread_message_count.findIndex(u => u.user === readBy);

                        if (userIndex > -1) {
                            // Replace existing user entry with count 0
                            room.unread_message_count[userIndex] = {
                                user: readBy,
                                count: 0,
                            };
                        } else {
                            // Add new entry
                            room.unread_message_count.push({
                                user: readBy,
                                count: 0,
                            });
                        }
                    }
                }
            });
        case types.UPDATE_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                const { currentRoom, updatedMessage } = payload;
                const messageIndex = draftState.selectedRoomMessages.findIndex(
                    value => value._id === updatedMessage._id,
                );
                const roomIndex = draftState.rooms.findIndex(room => room._id === currentRoom._id);
                if (messageIndex > -1) {
                    draftState.selectedRoomMessages[messageIndex] = updatedMessage;
                }
                if (roomIndex > -1) {
                    const currentRoomMessages = draftState.rooms[roomIndex].messages ?? [];
                    const messageIndexForRoom = currentRoomMessages.findIndex(
                        value => value._id === updatedMessage._id,
                    );
                    if (messageIndexForRoom > -1) {
                        currentRoomMessages[messageIndexForRoom] = updatedMessage;
                    } else {
                        currentRoomMessages.unshift(updatedMessage);
                    }
                }
            });
        case types.REACT_TO_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                const { room, updatedMessage } = payload;
                const messageIndex = draftState.selectedRoomMessages.findIndex(
                    value => value._id === updatedMessage._id,
                );
                const roomIndex = draftState.rooms.findIndex(r => r._id === room);
                if (messageIndex > -1) {
                    draftState.selectedRoomMessages[messageIndex] = updatedMessage;
                }
                if (roomIndex > -1) {
                    const currentRoomMessages = draftState.rooms[roomIndex].messages ?? [];
                    const messageIndexForRoom = currentRoomMessages.findIndex(
                        value => value._id === updatedMessage._id,
                    );
                    if (messageIndexForRoom > -1) {
                        currentRoomMessages[messageIndexForRoom] = updatedMessage;
                    } else {
                        currentRoomMessages.unshift(updatedMessage);
                    }
                }
            });
        case types.DELETE_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                const { currentRoom, messageId } = payload;
                const messageIndex = draftState.selectedRoomMessages.findIndex(value => value._id === messageId);
                const roomIndex = draftState.rooms.findIndex(room => room._id === currentRoom._id);
                const originalMessage = draftState.selectedRoomMessages[messageIndex];
                const deletedMessageBody = {
                    ...originalMessage,
                    deleted: true,
                };
                if (messageIndex > -1) {
                    draftState.selectedRoomMessages[messageIndex] = deletedMessageBody;
                }
                if (roomIndex > -1 && draftState.rooms[roomIndex]?.messages) {
                    const currentRoomMessages = draftState.rooms[roomIndex].messages;
                    const messageIndexForRoom = currentRoomMessages.findIndex(value => value._id === messageId);
                    if (messageIndexForRoom > -1) {
                        currentRoomMessages[messageIndexForRoom] = deletedMessageBody;
                    }
                }
            });
        case types.SEARCH_ROOMS:
            return produce(state, draftState => {
                draftState.searching = true;
                draftState.searchError = null;
                draftState.isSearchActive = true;
                draftState.searchTerm = payload.searchTerm;
            });
        case types.SEARCH_ROOMS_SUCCESS:
            return produce(state, draftState => {
                const { rooms, pagination } = payload;

                // Create a map of existing rooms by ID for quick reference
                const existingRoomsMap = new Map(draftState.rooms.map(room => [room._id, room]));

                // Process search results, preserving user data from existing rooms
                const processedRooms = rooms.map((room: any) => {
                    const existingRoom = existingRoomsMap.get(room._id);
                    if (existingRoom) {
                        // Preserve user data and important fields from the existing room
                        return {
                            ...existingRoom,
                            ...room,
                            // Ensure these fields are preserved to maintain ConversationView functionality
                            sender: existingRoom.sender || room.sender,
                            receiver: existingRoom.receiver || room.receiver,
                            messages: room.messages || existingRoom.messages,
                            matchReason: room.matchReason,
                            last_message_at: room.last_message_at || existingRoom.last_message_at,
                        };
                    }
                    return room;
                });

                // If it's the first page, replace rooms with processed results. Otherwise, append
                if (pagination.currentPage === 1) {
                    draftState.rooms = processedRooms;
                } else {
                    // For subsequent pages, we need to avoid duplicating rooms
                    const existingIds = new Set(draftState.rooms.map(r => r._id));
                    const newRooms = processedRooms.filter((room: any) => !existingIds.has(room._id));
                    draftState.rooms = [...draftState.rooms, ...newRooms];
                }

                draftState.roomsPagination = pagination;
                draftState.searching = false;
                draftState.fetchingRooms = false;
            });
        case types.SEARCH_ROOMS_FAIL:
            return produce(state, draftState => {
                draftState.searching = false;
                draftState.searchError = payload;
            });
        case types.QUEUE_FILE_UPLOAD:
            return produce(state, draftState => {
                // Ensure fileUploadQueue is an array
                if (!draftState.fileUploadQueue || !Array.isArray(draftState.fileUploadQueue)) {
                    draftState.fileUploadQueue = [];
                }
                draftState.fileUploadQueue.push(payload);
            });
        case types.UPLOAD_MESSAGE_FILE_SUCCESS:
            return produce(state, draftState => {
                const { messageId, fileUrls } = payload;
                const messageIndex = draftState.selectedRoomMessages.findIndex(msg => msg._id === messageId);

                if (messageIndex > -1) {
                    const message = draftState.selectedRoomMessages[messageIndex];

                    // Update appropriate field based on message type
                    if (message.message_type === 'picture_message') {
                        // Append to image array
                        if (!message.image) message.image = [];
                        message.image = fileUrls;
                        message.isLoading = false;
                    } else if (message.message_type === 'document_message') {
                        // Append to document array
                        if (!message.document) message.document = [];
                        message.document = fileUrls;
                        message.isLoading = false;
                    }

                    // Remove from queue - ensure it's an array before filtering
                    if (draftState.fileUploadQueue && Array.isArray(draftState.fileUploadQueue)) {
                        draftState.fileUploadQueue = draftState.fileUploadQueue.filter(
                            item => item.messageId !== messageId,
                        );
                    } else {
                        draftState.fileUploadQueue = [];
                    }
                }
            });
        case types.UPLOAD_MESSAGE_FILE_FAIL:
            return produce(state, draftState => {
                const { messageId } = payload;
                const messageIndex = draftState.selectedRoomMessages.findIndex(msg => msg._id === messageId);

                if (messageIndex > -1) {
                    draftState.selectedRoomMessages[messageIndex].uploadError = true;
                    draftState.selectedRoomMessages[messageIndex].isLoading = false;
                }

                // Remove from queue - ensure it's an array before filtering
                if (draftState.fileUploadQueue && Array.isArray(draftState.fileUploadQueue)) {
                    draftState.fileUploadQueue = draftState.fileUploadQueue.filter(
                        item => item.messageId !== messageId,
                    );
                } else {
                    draftState.fileUploadQueue = [];
                }
            });
        default:
            return state;
    }
};

export { reducer };
